import { PropsWithChildren } from 'react';

import { ApolloClient, gql, InMemoryCache } from '@apollo/client';
import { faLongArrowAltRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'next/link';
import { Button, Card, Col, Container, Pagination, Row } from 'react-bootstrap';
import Imgix from 'react-imgix';

import WhySkyhook from '@website/components/about/WhySkyhook';
import CardPost from '@website/components/directoryTheme/CardPost';
import DefaultLayout from '@website/components/layouts/DefaultLayout';
import { getImageUrl } from '@website/components/media/utils/imgix';
import { SalesPopupProvider } from '@website/components/SalesPopupProvider';
import SeoMetaTags from '@website/components/seoMetaTags';

const client = new ApolloClient({
  uri: `https://graphql.contentful.com/content/v1/spaces/${process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID}/environments/master?access_token=${process.env.NEXT_PUBLIC_CONTENTFUL_ACCESS_TOKEN}`,
  cache: new InMemoryCache(),
});

const allBlogsQuery = gql`
  query ($limit: Int!, $skip: Int!) {
    blogPostCollection(
      order: sys_firstPublishedAt_DESC
      limit: $limit
      skip: $skip
    ) {
      total
      items {
        sys {
          firstPublishedAt
        }
        urlSlug
        title
        headerImage {
          url
        }
      }
    }
  }
`;

interface BlogPost {
  sys: {
    firstPublishedAt: Date;
  };
  urlSlug: string;
  title: string;
  headerImage: {
    url: string;
  };
}

/**
 * Get Blog Posts
 */
export async function getStaticProps() {
  const res = await client.query({
    query: allBlogsQuery,
    fetchPolicy: 'no-cache',
    variables: {
      limit: 50,
      skip: 0,
    },
  });

  const posts = await res.data.blogPostCollection.items;
  const total = await res.data.blogPostCollection.total;

  return {
    props: {
      posts,
      total,
    },
    revalidate: 60 * 30, // 30 minutes
  };
}

export default function Blogs({
  posts,
  total,
}: {
  posts: BlogPost[];
  total: number;
}) {
  const featuredPost: BlogPost = posts[0];

  const pages = Array.from({ length: Math.ceil(total / 50) }, (_, i) => i + 1);

  return (
    <>
      <SeoMetaTags
        title="The Skyhook Magazine"
        description="The Skyhook Magazine is a collection of stories from our community of local guides and travellers."
        showWebsiteName={false}
      />

      <style jsx global>{`
        .hero-blog {
          width: 100% !important;
          aspect-ratio: 1/1 !important;
          display: flex;
          justify-content: center;
          flex-direction: column;
          align-items: center;
        }

        @media (min-width: 768px) {
          .hero-blog {
            aspect-ratio: 5/3 !important;
          }
        }

        @media (min-width: 992px) {
          .hero-blog {
            aspect-ratio: 5/2 !important;
          }
        }

        @media (min-width: 1200px) {
          .hero-blog {
            aspect-ratio: 5/1.7 !important;
          }
        }
      `}</style>

      {featuredPost && (
        <section className="position-relative hero-blog">
          <Imgix
            src={`${getImageUrl(
              featuredPost.headerImage.url,
              process.env.NEXT_PUBLIC_CONTENTFUL_IMGIX_URL,
            )}`}
            imgixParams={{
              auto: 'compress,format,enhance,redeye',
              crop: 'faces',
              fit: 'crop',
              ar: '1:1',
            }}
            sizes="100vw"
            className="w-100 h-100 bg-image"
          />
          <Container className="position-relative">
            <Row>
              <Col lg="6">
                <Card>
                  <Card.Body className="p-5">
                    <strong className="text-uppercase text-secondary d-inline-block mb-2 text-sm">
                      {/* {featuredPost.subtitle} */}
                    </strong>
                    <h2 className="mb-3">{featuredPost.title}</h2>
                    {/* <p className="text-muted">{featuredPost.content}</p> */}
                    <Link href={`/blog/${featuredPost.urlSlug}`}>
                      <Button variant="link" className="p-0">
                        Read post <FontAwesomeIcon icon={faLongArrowAltRight} />
                      </Button>
                    </Link>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>
      )}
      <section className="py-3 pt-6">
        <Container>
          <Row className="mb-5">
            {posts.map((post, index) => {
              // The first post is featured so we skip it
              if (index === 0) return null;

              return (
                <Col
                  key={post.urlSlug}
                  sm="6"
                  lg="4"
                  className="mb-4 hover-animate"
                >
                  <CardPost post={post} />
                </Col>
              );
            })}
          </Row>
          {pages.length > 1 && (
            <Pagination aria-label="Page navigation example">
              {pages.map((page) => (
                <Link
                  href={`/blog/page/${page}`}
                  key={page}
                  legacyBehavior
                  passHref
                >
                  <Pagination.Item active={page === 1} className="text-sm">
                    {page}
                  </Pagination.Item>
                </Link>
              ))}
            </Pagination>
          )}
        </Container>
      </section>

      <WhySkyhook />

      <SalesPopupProvider bottomPosition={10} />
    </>
  );
}

Blogs.Layout = function Layout({ children }: PropsWithChildren<{}>) {
  return <DefaultLayout className="">{children}</DefaultLayout>;
};
